import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import MasterConfig from '../../config/Master';
import { spacing, getBorders, getBackgroundTheme } from '../../style';
import Container from './Container';
import CookieBanner from './CookieBanner';
import { sendSocialAction } from '../../actions/socialAction';
import { isDarkMode, termsOfUseUrl } from '../../utils/helpers';
import useMixPanel from '../../hooks/useMixPanel';
import GDPRDarkIcon from '../../atoms/icons/GDPRDarkIcon';
import GDPRLightIcon from '../../atoms/icons/GDPRLightIcon';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import breakpoints from '../../theme/material/breakpoints';
import useCookieConsentCheck from '../../hooks/useCookieConsentCheck';

const iconSize = 20;
const copyDate = new Date().getFullYear();

const StyledContainer = styled(Container)({
  padding: '15px 15px',
  [breakpoints.down('sm')]: {
    padding: '20px 20px',
    paddingBottom: 0,
  },
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const FooterDiv = styled('div')(({ isLogin }) => ({
  borderTop: isLogin ? '1px solid #DAE1E9' : getBorders().DEFAULT,
  position: 'absolute',
  width: '100%',
  bottom: 0,
  background: isLogin ? '#F6F4F4' : isDarkMode() ? '#212121' : '#F6F4F4',
  zIndex: 995,
}));

const LinksContainer = styled('nav')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-stat',
  flexDirection: 'row',
  [breakpoints.down('sm')]: {
    alignItems: 'center',
    flexDirection: 'column',
  },
});

const ContainerGDPR = styled('div')({
  ...spacing.MARGIN_0,
  whiteSpace: 'nowrap',
  padding: 0,
  margin: '0px 15px 0 0',
  [breakpoints.down('sm')]: {
    textAlign: 'center',
    margin: '30px 0 0 0',
  },
});

const Icon = styled('svg')({
  width: iconSize,
  height: iconSize,
  fill: 'none',
  verticalAlign: 'middle',
});

const SocialList = styled('ul')({
  ...spacing.MARGIN_0,
  whiteSpace: 'nowrap',
  padding: '5px 15px 20px 0',
  [breakpoints.down('sm')]: {
    textAlign: 'center',
    padding: '30px 0px 24px 0',
  },
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const CopyrightText = styled('span')(() => ({
  fontSize: 11,
  fontWeight: 400,
  lineHeight: '16px',
  color: isDarkMode() ? '#AFB0B1' : '#8F8F8F',
  display: 'block',
  marginBottom: '2px',
  [breakpoints.down('sm')]: {
    textAlign: 'center',
    paddingBottom: '18px',
  },
}));

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const DisclaimerText = styled('span')(() => ({
  fontSize: 13,
  fontWeight: 400,
  lineHeight: '16px',
  marginBottom: 11,
  display: 'block',
  color: isDarkMode() ? '#AFB0B1' : '#8F8F8F',
  [breakpoints.down('sm')]: {
    textAlign: 'center',
    paddingBottom: '0',
    marginTop: 0,
    marginBottom: 28,
  },
}));

const Link = styled('a')(() => ({
  display: 'inline-block',
  padding: '14px 10px',
  letterSpacing: '-0.26px',
  color: getBackgroundTheme().footerLinks,
  [breakpoints.down('sm')]: {
    display: 'block',
    textAlign: 'center',
  },
}));

const PrivacyPreferencesLink = styled(RouterLink)(() => ({
  color: getBackgroundTheme().footerLinks,
  textDecoration: 'underline',
  fontSize: '13px',
  paddingBottom: '24px',
  [breakpoints.down('sm')]: {
    paddingBottom: 0,
  },
}));

const ListContainer = styled('ul')({
  margin: 0,
  listStyle: 'none',
  padding: '7px 0 0 0px',
  display: 'inline-flex',
  gap: 16,

  [breakpoints.down('sm')]: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    flexWrap: 'wrap',
    padding: 0,
    alignSelf: 'stretch',
    gap: 16,
  },
  '> li': {
    [breakpoints.down('sm')]: {
      lineHeight: '10px',
    },
  },
  '> li a': {
    fontSize: 14,
    padding: 0,
    whiteSpace: 'nowrap',
    fontWeight: 600,
    letterSpacing: '-0.26px',
  },
});

const IconLink = styled(Link)({
  lineHeight: `${iconSize}px`,
  padding: 0,
});

const SocialItem = styled('li')({
  display: 'inline-block',
});

const setCorrectVisitAction = (e, pageType) => {
  sendSocialAction(e, pageType.toUpperCase());
};

const triggerVisitTracking = (visitType, ref) => {
  if (ref && ref.current) {
    ref.current.addEventListener('click', e => setCorrectVisitAction(e, visitType), false);

    return () => {
      ref.current.removeEventListener('click', () => null, false);
    };
  }
};

interface FooterProps {
  isFooterless?: boolean;
  isLogin?: boolean;
  profile?: Record<string, unknown>;
  customStyle?: React.CSSProperties;
}

const Footer = ({ isFooterless, isLogin, ...rest }: FooterProps): JSX.Element => {
  const visitFacebook = useRef(null);
  const visitTwitter = useRef(null);
  const visitInstagram = useRef(null);
  const visitTerms = useRef(null);
  const visitTermsEU = useRef(null);
  const visitPrivacy = useRef(null);
  const visitFaq = useRef(null);
  const visitBlog = useRef(null);
  const visitTips = useRef(null);
  const visitCommunityGuide = useRef(null);
  const visitCommitmentToSafety = useRef(null);
  const iconColor = isDarkMode() ? '#6A6A6A' : '#8F8F8F';
  const { trackPageView } = useMixPanel();
  const { isMobile } = useMediaBreakpoints();
  const { isQualified } = useCookieConsentCheck();
  const location = useLocation();

  // Check if current page is an unauthenticated page (login or join)
  const isUnauthenticatedPage = location.pathname === '/login' || location.pathname === '/join';

  useEffect(() => {
    triggerVisitTracking('facebook', visitFacebook);
    triggerVisitTracking('twitter', visitTwitter);
    triggerVisitTracking('instagram', visitInstagram);
    triggerVisitTracking('terms', visitTerms);
    triggerVisitTracking('terms-eu', visitTermsEU);
    triggerVisitTracking('privacy', visitPrivacy);
    triggerVisitTracking('faq', visitFaq);
    triggerVisitTracking('blog', visitBlog);
    triggerVisitTracking('tips', visitTips);
    triggerVisitTracking('community guidelines', visitCommunityGuide);
    triggerVisitTracking('commitment to safety', visitCommitmentToSafety);
  }, []);

  const renderGDPRCompliant = () => {
    return <ContainerGDPR>{isDarkMode() ? <GDPRDarkIcon /> : <GDPRLightIcon />}</ContainerGDPR>;
  };

  const handleTrackPageView = (path: string) => {
    trackPageView(path);
  };

  const handlePrivacyPreferencesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleTrackPageView('privacy-preferences');

    // Step 1: Click user-nav-item to open the sidebar (but keep it hidden)
    const userNavItem = document.getElementById('user-nav-item') as HTMLElement;

    if (!userNavItem) {
      console.error('Privacy Preferences navigation failed: user-nav-item element not found');
      return;
    }

    // Hide the sidebar immediately before opening it
    const sidebarParent = document.getElementById('sidebar-parent');

    if (!sidebarParent) {
      console.error('Privacy Preferences navigation failed: sidebar-parent element not found');
      return;
    }

    sidebarParent.style.visibility = 'hidden';
    userNavItem.click();

    // Step 2: Wait briefly for sidebar DOM to be ready, then trigger all clicks at once
    setTimeout(() => {
      // Click Account Settings-Section immediately
      const accountSettingsMenuItem = document.getElementById('Account Settings-Section') as HTMLElement;

      if (!accountSettingsMenuItem) {
        console.error('Privacy Preferences navigation failed: Account Settings-Section element not found');
        if (sidebarParent) {
          sidebarParent.style.visibility = 'visible';
        }
        return;
      }

      accountSettingsMenuItem.click();

      // Click Privacy Preferences immediately (no delay)
      setTimeout(() => {
        const privacyPreferencesMenuItem = document.getElementById('Privacy Preferences') as HTMLElement;

        if (!privacyPreferencesMenuItem) {
          console.error('Privacy Preferences navigation failed: Privacy Preferences element not found');
          if (sidebarParent) {
            sidebarParent.style.visibility = 'visible';
          }
          return;
        }

        privacyPreferencesMenuItem.click();

        // Show the sidebar after a brief delay to ensure everything is rendered
        setTimeout(() => {
          if (sidebarParent) {
            sidebarParent.style.visibility = 'visible';
          }
        }, 100);
      }, 50); // Very short delay just to ensure Account Settings DOM is ready
    }, 100); // Short delay to ensure sidebar DOM is ready
  };

  if (isFooterless) {
    return null;
  }

  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <FooterDiv isLogin={isLogin} {...rest}>
      <CookieBanner />
      <StyledContainer wideHeader>
        <LinksContainer>
          <ListContainer>
            <li>
              <Link
                ref={visitTerms}
                rel="noopener"
                target="_blank"
                href={termsOfUseUrl(false)}
                onClick={() => handleTrackPageView('terms')}
              >
                Terms
              </Link>
            </li>
            <li>
              <Link
                ref={visitTermsEU}
                rel="noopener"
                target="_blank"
                href={termsOfUseUrl(true)}
                onClick={() => handleTrackPageView('term_eu')}
              >
                Terms (EU)
              </Link>
            </li>
            <li>
              <Link
                ref={visitPrivacy}
                rel="noopener"
                target="_blank"
                href={`${MasterConfig.FULL_URL}/privacy`}
                onClick={() => handleTrackPageView('privacy')}
              >
                Privacy
              </Link>
            </li>
            <li>
              <Link
                ref={visitFaq}
                rel="noopener"
                target="_blank"
                href={`${MasterConfig.FULL_URL}/faq`}
                onClick={() => handleTrackPageView('faq')}
              >
                FAQ&thinsp;/&thinsp;Help
              </Link>
            </li>
            <li>
              <Link
                ref={visitBlog}
                rel="noopener"
                target="_blank"
                href={`${MasterConfig.FULL_URL}/blog`}
                onClick={() => handleTrackPageView('blog')}
              >
                Blog
              </Link>
            </li>
            <li>
              <Link
                ref={visitTips}
                rel="noopener"
                target="_blank"
                href="https://whatsyourprice.com/blog/tips-for-dating-safely-during-covid-and-after/"
                onClick={() => handleTrackPageView('tips')}
              >
                Tips for Dating Safely
              </Link>
            </li>
            <li>
              <Link
                ref={visitCommunityGuide}
                rel="noopener"
                target="_blank"
                href={`${MasterConfig.FULL_URL}/community-guidelines`}
                onClick={() => handleTrackPageView('guidelines')}
              >
                Community Guidelines
              </Link>
            </li>
            <li>
              <Link
                ref={visitCommitmentToSafety}
                rel="noopener"
                target="_blank"
                href={`${MasterConfig.FULL_URL}/safety/commitment-to-safety`}
                onClick={() => handleTrackPageView('commitment')}
              >
                Commitment to Safety
              </Link>
            </li>
          </ListContainer>
          <SocialList>
            <SocialItem>
              <IconLink
                ref={visitFacebook}
                rel="noopener"
                href={MasterConfig.SOCIAL_LINKS.FACEBOOK}
                target="_blank"
                style={{ marginRight: 30 }}
                onClick={() => handleTrackPageView('facebook')}
              >
                <Icon width={iconSize} height={21} viewBox="0 0 20 21">
                  <path
                    d="M19.9992 10.1878C19.9992 4.66426 15.5223 0.187408 9.99958 0.187408C4.47685 0.187408 0 4.66426 0 10.1878C0 15.178 3.65682 19.3149 8.43702 20.0649V13.0779H5.89858V10.187H8.43702V7.9844C8.43702 5.47763 9.93041 4.0934 12.2138 4.0934C13.3081 4.0934 14.4523 4.28841 14.4523 4.28841V6.74935H13.1922C11.9505 6.74935 11.563 7.52021 11.563 8.31108V10.1878H14.3364L13.8931 13.0788H11.563V20.0657C16.3432 19.3157 20 15.1789 20 10.1878H19.9992Z"
                    fill={iconColor}
                  />
                </Icon>
              </IconLink>
            </SocialItem>
            <SocialItem>
              <IconLink
                ref={visitTwitter}
                rel="noopener"
                href={MasterConfig.SOCIAL_LINKS.TWITTER}
                target="_blank"
                style={{ marginRight: 30 }}
                onClick={() => handleTrackPageView('twitter')}
              >
                <Icon width={iconSize} height={21} viewBox="0 0 20 21">
                  <g clipPath="url(#a)">
                    <path
                      fill={iconColor}
                      d="M11.903 8.569 19.348 0h-1.764l-6.465 7.44L5.955 0H0l7.808 11.25L0 20.238h1.764l6.828-7.857 5.452 7.857H20L11.902 8.569ZM9.486 11.35l-.791-1.12L2.4 1.315h2.71l5.08 7.194.791 1.12 6.604 9.352h-2.71l-5.389-7.63v-.001Z"
                    />
                  </g>
                  <defs>
                    <clipPath id="a">
                      <path fill="#fff" d="M0 0h20v20.253H0z" />
                    </clipPath>
                  </defs>
                </Icon>
              </IconLink>
            </SocialItem>
            <SocialItem>
              <IconLink
                ref={visitInstagram}
                rel="noopener"
                href={MasterConfig.SOCIAL_LINKS.INSTAGRAM}
                target="_blank"
                onClick={() => handleTrackPageView('instagram')}
              >
                <Icon width={iconSize} height={21} viewBox="0 0 20 21">
                  <path
                    d="M10 0.126587C7.28333 0.126587 6.94417 0.139087 5.8775 0.186587C4.8125 0.236587 4.0875 0.404087 3.45 0.651587C2.7925 0.906587 2.23417 1.24909 1.67833 1.80492C1.1225 2.36075 0.779167 2.91825 0.525 3.57659C0.2775 4.21409 0.109167 4.93909 0.06 6.00409C0.01 7.07075 0 7.40992 0 10.1266C0 12.8433 0.0125 13.1824 0.06 14.2491C0.11 15.3133 0.2775 16.0391 0.525 16.6766C0.776077 17.344 1.16966 17.9486 1.67833 18.4483C2.17773 18.9573 2.78241 19.3509 3.45 19.6016C4.08833 19.8483 4.81333 20.0174 5.8775 20.0666C6.94417 20.1166 7.28333 20.1266 10 20.1266C12.7167 20.1266 13.0558 20.1141 14.1225 20.0666C15.1867 20.0166 15.9125 19.8483 16.55 19.6016C17.2172 19.3502 17.8218 18.9567 18.3217 18.4483C18.8309 17.949 19.2245 17.3443 19.475 16.6766C19.7217 16.0391 19.8908 15.3133 19.94 14.2491C19.99 13.1824 20 12.8433 20 10.1266C20 7.40992 19.9875 7.07075 19.94 6.00409C19.89 4.93992 19.7217 4.21325 19.475 3.57659C19.2238 2.90927 18.8302 2.30469 18.3217 1.80492C17.8226 1.29545 17.2178 0.901745 16.55 0.651587C15.9125 0.404087 15.1867 0.235754 14.1225 0.186587C13.0558 0.136587 12.7167 0.126587 10 0.126587ZM10 1.92659C12.6692 1.92659 12.9875 1.93992 14.0417 1.98575C15.0167 2.03159 15.5458 2.19325 15.8975 2.33159C16.3658 2.51242 16.6975 2.72909 17.0492 3.07825C17.3983 3.42825 17.615 3.76075 17.7958 4.22909C17.9325 4.58075 18.0958 5.10992 18.14 6.08492C18.1875 7.13992 18.1983 7.45659 18.1983 10.1266C18.1983 12.7966 18.1858 13.1141 18.1367 14.1683C18.0858 15.1433 17.9233 15.6724 17.7858 16.0241C17.6232 16.4581 17.3675 16.8511 17.0367 17.1758C16.7134 17.5068 16.3206 17.7619 15.8867 17.9224C15.5367 18.0591 14.9992 18.2224 14.0242 18.2666C12.9625 18.3141 12.65 18.3249 9.975 18.3249C7.29917 18.3249 6.98667 18.3124 5.92583 18.2633C4.95 18.2124 4.4125 18.0499 4.0625 17.9124C3.62825 17.7518 3.23556 17.4957 2.91333 17.1633C2.57882 16.8422 2.32226 16.4488 2.16333 16.0133C2.02583 15.6633 1.86417 15.1258 1.81333 14.1508C1.77583 13.1008 1.7625 12.7766 1.7625 10.1141C1.7625 7.45075 1.77583 7.12575 1.81333 6.06325C1.86417 5.08825 2.02583 4.55159 2.16333 4.20159C2.33833 3.72659 2.5625 3.40159 2.91333 3.05075C3.2625 2.70159 3.58833 2.47659 4.0625 2.30242C4.4125 2.16409 4.93833 2.00159 5.91333 1.95159C6.97583 1.91409 7.28833 1.90159 9.9625 1.90159L10 1.92659ZM10 4.99159C9.32566 4.99159 8.65793 5.12441 8.03492 5.38247C7.41191 5.64052 6.84584 6.01876 6.36901 6.49559C5.89218 6.97242 5.51394 7.5385 5.25588 8.16151C4.99782 8.78451 4.865 9.45225 4.865 10.1266C4.865 10.8009 4.99782 11.4687 5.25588 12.0917C5.51394 12.7147 5.89218 13.2808 6.36901 13.7576C6.84584 14.2344 7.41191 14.6126 8.03492 14.8707C8.65793 15.1288 9.32566 15.2616 10 15.2616C11.3619 15.2616 12.668 14.7206 13.631 13.7576C14.594 12.7946 15.135 11.4885 15.135 10.1266C15.135 8.7647 14.594 7.45859 13.631 6.49559C12.668 5.53259 11.3619 4.99159 10 4.99159ZM10 13.4599C8.15833 13.4599 6.66667 11.9683 6.66667 10.1266C6.66667 8.28492 8.15833 6.79325 10 6.79325C11.8417 6.79325 13.3333 8.28492 13.3333 10.1266C13.3333 11.9683 11.8417 13.4599 10 13.4599ZM16.5383 4.78909C16.5268 5.09961 16.3953 5.39355 16.1715 5.60914C15.9477 5.82474 15.6491 5.94519 15.3383 5.94519C15.0276 5.94519 14.729 5.82474 14.5052 5.60914C14.2814 5.39355 14.1499 5.09961 14.1383 4.78909C14.1383 4.47083 14.2648 4.1656 14.4898 3.94056C14.7148 3.71552 15.0201 3.58909 15.3383 3.58909C15.6566 3.58909 15.9618 3.71552 16.1869 3.94056C16.4119 4.1656 16.5383 4.47083 16.5383 4.78909Z"
                    fill={iconColor}
                  />
                </Icon>
              </IconLink>
            </SocialItem>
            {isMobile && renderGDPRCompliant()}
            {isQualified && isMobile && !isUnauthenticatedPage && (
              <div style={{ marginTop: '10px' }}>
                <PrivacyPreferencesLink to="#" onClick={handlePrivacyPreferencesClick}>
                  Do not sell or share my personal information
                </PrivacyPreferencesLink>
              </div>
            )}
          </SocialList>
        </LinksContainer>
        <LinksContainer>
          {isQualified && !isMobile && !isUnauthenticatedPage && (
            <PrivacyPreferencesLink to="#" onClick={handlePrivacyPreferencesClick}>
              Do not sell or share my personal information
            </PrivacyPreferencesLink>
          )}
        </LinksContainer>
        <LinksContainer>
          <div>
            <DisclaimerText data-nosnippet>
              {`Members of ${MasterConfig.SITE_NAME} are not subject to mandatory background screenings but have the option to submit for an ID verification`}
            </DisclaimerText>
            <CopyrightText data-nosnippet>
              Copyright © 2010-
              {copyDate} {MasterConfig.BASE_URL} in conjunction with W8 Tech Limited and their related companies
            </CopyrightText>
          </div>
          {!isMobile && renderGDPRCompliant()}
        </LinksContainer>
      </StyledContainer>
    </FooterDiv>
  );
};

export default Footer;
